import React, { createContext, useContext, useRef } from 'react';
import PostHog from 'posthog-react-native';
import Constants from 'expo-constants';

const POSTHOG_API_KEY = Constants.expoConfig?.extra?.EXPO_PUBLIC_POSTHOG_API_KEY || '';
const POSTHOG_HOST = 'https://eu.i.posthog.com';

const PostHogContext = createContext<PostHog | null>(null);

export function usePostHog(): PostHog | null {
  return useContext(PostHogContext);
}

interface PostHogProviderProps {
  children: React.ReactNode;
}

/**
 * PostHog analytics provider. Wraps the app and initializes the PostHog client.
 * Safe to use without an API key — all calls become no-ops when key is missing.
 */
export function PostHogProvider({ children }: PostHogProviderProps) {
  const clientRef = useRef<PostHog | null>(null);

  if (!clientRef.current && POSTHOG_API_KEY) {
    clientRef.current = new PostHog(POSTHOG_API_KEY, {
      host: POSTHOG_HOST,
      // Flush events every 30s or when 20 events are queued
      flushAt: 20,
      flushInterval: 30000,
      // Capture app lifecycle events automatically
      captureNativeAppLifecycleEvents: true,
      // Capture screen views automatically via React Navigation
      captureScreens: false, // We'll do this manually for more control
    });
  }

  return (
    <PostHogContext.Provider value={clientRef.current}>
      {children}
    </PostHogContext.Provider>
  );
}
